import { type ApolloClient } from 'apollo-client';
import gql from 'graphql-tag';
import type { DocumentNode } from 'graphql';
import { createAppAri } from '@atlassian/cs-ari';
import { useTracingContext } from '../../../error-reporting';
import { useCallback } from 'react';

export interface useForgeAppQueryProps {
	client: ApolloClient<object>;
}

export const APP_BY_ID_QUERY: DocumentNode = gql`
	query appById($appAri: ID!) {
		app(id: $appAri) {
			name
			privacyPolicy
			termsOfService
		}
	}
`;

export interface AppData {
	name: string;
	privacyPolicy: string;
	termsOfService: string;
}

export class ForgeAppQueryError extends Error {}

interface AppByIdQueryVariables {
	appAri: string;
}

interface AppByIdQueryData {
	app: AppData;
}

export function useForgeAppQuery({
	client,
}: useForgeAppQueryProps): (appId: string) => Promise<AppData> {
	const tracing = useTracingContext();
	const fetchAppData = async (appId: string): Promise<AppData> => {
		const appAri = createAppAri({ appId }).toString();
		tracing?.recordGqlCall('APP_BY_ID_QUERY - start');

		const { data, errors } = await client.query<AppByIdQueryData, AppByIdQueryVariables>({
			query: APP_BY_ID_QUERY,
			variables: {
				appAri,
			},
		});
		tracing?.recordGqlCall('APP_BY_ID_QUERY - end');

		if (errors) {
			const details = JSON.stringify(errors);
			throw new ForgeAppQueryError(
				`Failed to fetch app data by its ID: ${appAri}, GraphQLError: ${details}`,
			);
		}
		return data.app;
	};
	return useCallback(fetchAppData, [client, tracing]);
}
